import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import gradeService from "../../services/gradeService";
import semesterService from "../../services/semesterService";
import subjectService from "../../services/subjectService";
import classService from "../../services/classService";
import { getProfessionPillClasses } from "../../utils/professionColors";
import { getInitials } from "../../utils/stringUtils";
import { getGradeColor } from "../../utils/gradeColors";
import GradesChart from "../grades/GradesChart";
import ReportsList from "../grades/ReportsList";
import { getCurrentSemester } from "../../utils/dateUtils";
import practiceInstructorService from "../../services/practiceInstructorService";
import PracticeInstructorSelectModal from "../common/PracticeInstructorSelectModal";
import StudentGradeReportModal from "./StudentGradeReportModal";
// import LoadingSpinner from "../common/LoadingSpinner";
import { cache, cacheKeys } from "../../utils/cacheUtils";
import { SkeletonStudentDetailPage } from "../common/SkeletonCard";

const formatDate = (iso) => {
  if (!iso) return "N/A";
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const getSemesterNumber = (semesterName) => {
  return parseInt(semesterName?.match(/\d+/)?.[0] || "0");
};

function StudentDetail() {
  const { user } = useAuth();
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [canViewGrades, setCanViewGrades] = useState(true);

  // School-based data structure
  const [schoolData, setSchoolData] = useState([]); // [{schoolId, schoolName, classes, semesters, grades, subjects, selectedSemesters}]
  const [loadingStates, setLoadingStates] = useState({}); // {schoolId: boolean}

  const [currentInstructor, setCurrentInstructor] = useState(null);

  // Modal state for semester filtering
  const [semesterModalOpen, setSemesterModalOpen] = useState(null); // schoolId of open modal or 'overall' for top chart

  // Overall chart semester selection (across all schools)
  const [overallSelectedSemesters, setOverallSelectedSemesters] = useState([]);

  // Practice Instructor Assignment
  const [assignMode, setAssignMode] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [gradeReportModalOpen, setGradeReportModalOpen] = useState(false);

  const isStrictlyPracticeInstructor =
    user?.roles?.some(
      (r) => r.name === "PRACTICE_INSTRUCTOR" || r.name === "PRAXISBILDNER",
    ) &&
    !user?.roles?.some((r) => r.name === "ADMIN" || r.name === "BERUFSBILDNER");

  // Access: Admin oder Berufsbildner oder Praxisbildner
  const hasAccess = () =>
    user?.roles?.some(
      (r) =>
        r.name === "ADMIN" ||
        r.name === "BERUFSBILDNER" ||
        r.name === "PRACTICE_INSTRUCTOR" ||
        r.name === "PRAXISBILDNER",
    );

  useEffect(() => {
    if (!hasAccess()) {
      setError(
        "Access denied. This feature is only available for administrators and instructors.",
      );
      setLoading(false);
      return;
    }
    loadStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, user?.roles]);

  useEffect(() => {
    if (student) {
      loadAllSchoolData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  const loadStudentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const studentData = sessionStorage.getItem(`student-${studentId}`);
      if (studentData) {
        let parsedStudent = JSON.parse(studentData);

        // Check Grade Access for Practice Instructors
        if (isStrictlyPracticeInstructor) {
          try {
            const myStudents = await practiceInstructorService.getMyStudents();
            const isAssigned = myStudents.some(
              (s) => s.id === parseInt(studentId),
            );
            setCanViewGrades(isAssigned);
          } catch (err) {
            console.error(
              "Failed to verify assignment status for grades access",
              err,
            );
            setCanViewGrades(false);
          }
        }

        // Fetch classes if missing (e.g. for Practice Instructors)
        if (!parsedStudent.classes) {
          try {
            const classes = await classService.getClassesByUserId(studentId);
            parsedStudent = { ...parsedStudent, classes: classes };
          } catch (err) {
            console.warn("Failed to fetch classes for student:", err);
            // Non-blocking, might just show empty data
          }
        }

        setStudent(parsedStudent);

        // Fetch current instructor
        try {
          const instructor =
            await practiceInstructorService.getCurrentInstructor(studentId);
          setCurrentInstructor(instructor);
        } catch (e) {
          console.warn("Failed to fetch assigned instructor", e);
          setCurrentInstructor(null);
        }
      } else {
        setError("Student not found. Please return to the student list.");
        setLoading(false);
        return;
      }
    } catch (e) {
      console.error("Error loading student data", e);
      setError("Failed to load student data.");
    } finally {
      setLoading(false);
    }
  };

  const loadAllSchoolData = async () => {
    if (!student?.classes) return;

    // Group classes by school
    const schoolMap = new Map();
    student.classes.forEach((classInfo) => {
      if (!schoolMap.has(classInfo.schoolId)) {
        schoolMap.set(classInfo.schoolId, {
          schoolId: classInfo.schoolId,
          schoolName: classInfo.schoolName,
          classes: [],
        });
      }
      schoolMap.get(classInfo.schoolId).classes.push(classInfo);
    });

    const schools = Array.from(schoolMap.values());

    // Initialize loading states
    const initialLoadingStates = {};
    schools.forEach((school) => {
      initialLoadingStates[school.schoolId] = true;
    });
    setLoadingStates(initialLoadingStates);

    // 🚀 OPTIMIZATION: Load grades ONCE for all schools (cached)
    const gradesPromise = (async () => {
      if (isStrictlyPracticeInstructor && !canViewGrades) return [];

      const cacheKey = cacheKeys.grades(studentId);
      const cached = cache.get(cacheKey);
      if (cached) return cached;

      try {
        const grades = await gradeService.getGradesByUser(parseInt(studentId));
        cache.set(cacheKey, grades);
        return grades;
      } catch (e) {
        console.warn("Could not load grades:", e);
        return [];
      }
    })();

    // 🚀 OPTIMIZATION: Load data for each school IN PARALLEL
    const schoolDataPromises = schools.map(async (school) => {
      try {
        // 🚀 Parallel fetch: class details
        const classesWithDetails = await Promise.all(
          school.classes.map(async (classInfo) => {
            const cacheKey = cacheKeys.class(classInfo.id);
            const cached = cache.get(cacheKey);
            if (cached) return { ...classInfo, ...cached };

            try {
              const fullClassData = await classService.getClassById(
                classInfo.id,
              );
              const classData = {
                startDate: fullClassData?.startDate,
                endDate: fullClassData?.endDate,
              };
              cache.set(cacheKey, classData);
              return { ...classInfo, ...classData };
            } catch (e) {
              return classInfo;
            }
          }),
        );

        // 🚀 Parallel fetch: all semesters for all classes
        const semesterPromises = classesWithDetails.map(async (classInfo) => {
          const cacheKey = cacheKeys.semesters(school.schoolId, classInfo.id);
          const cached = cache.get(cacheKey);
          if (cached) {
            return cached.map((semester) => ({
              ...semester,
              classId: classInfo.id,
              className: classInfo.name,
              classInfo: classInfo,
            }));
          }

          try {
            const classSemesters = await semesterService.getSemestersByClass(
              school.schoolId,
              classInfo.id,
            );
            if (classSemesters) {
              cache.set(cacheKey, classSemesters);
              return classSemesters.map((semester) => ({
                ...semester,
                classId: classInfo.id,
                className: classInfo.name,
                classInfo: classInfo,
              }));
            }
            return [];
          } catch (e) {
            console.warn(
              `Could not load semesters for class ${classInfo.name}:`,
              e,
            );
            return [];
          }
        });

        const semesterResults = await Promise.all(semesterPromises);
        const allSemesters = semesterResults.flat();

        // Remove duplicates based on semester ID and class ID combination
        const uniqueSemesters = allSemesters.filter(
          (semester, index, self) =>
            index ===
            self.findIndex(
              (s) => s.id === semester.id && s.classId === semester.classId,
            ),
        );

        // Calculate current semesters for each class and auto-select them
        const currentSemesterIds = new Set();

        for (const classInfo of classesWithDetails) {
          if (classInfo.startDate) {
            const currentSem = getCurrentSemester(classInfo.startDate);

            if (currentSem && currentSem.semesterNumber) {
              const matchingSemesters = uniqueSemesters.filter((sem) => {
                if (sem.classId !== classInfo.id) return false;
                const semesterNum = parseInt(
                  sem.name?.match(/\d+/)?.[0] || "0",
                );
                return semesterNum === currentSem.semesterNumber;
              });

              matchingSemesters.forEach((sem) =>
                currentSemesterIds.add(sem.id),
              );
            }
          }
        }

        const selectedSemesters = Array.from(currentSemesterIds);

        // 🚀 Wait for grades (shared across all schools)
        const allGrades = await gradesPromise;

        // 🚀 Parallel fetch: subjects for all class/semester combinations
        const subjectPromises = [];
        for (const classInfo of classesWithDetails) {
          for (const semester of uniqueSemesters) {
            const cacheKey = cacheKeys.subjects(
              school.schoolId,
              classInfo.id,
              semester.id,
            );
            const cached = cache.get(cacheKey);

            if (cached) {
              subjectPromises.push(Promise.resolve(cached));
            } else {
              subjectPromises.push(
                subjectService
                  .getSubjectsBySchoolClassSemester(
                    school.schoolId,
                    classInfo.id,
                    semester.id,
                  )
                  .then((subjects) => {
                    if (subjects) cache.set(cacheKey, subjects);
                    return subjects || [];
                  })
                  .catch(() => []),
              );
            }
          }
        }

        const subjectResults = await Promise.all(subjectPromises);
        let allSubjects = subjectResults.flat();

        // Remove duplicates based on subject ID
        allSubjects = allSubjects.filter(
          (subject, index, self) =>
            index === self.findIndex((s) => s.id === subject.id),
        );

        // Fallback: if no subjects found, get all subjects
        if (allSubjects.length === 0) {
          const cacheKey = cacheKeys.allSubjects();
          const cached = cache.get(cacheKey);
          if (cached) {
            allSubjects = cached;
          } else {
            try {
              allSubjects = await subjectService.getAllSubjects();
              cache.set(cacheKey, allSubjects);
            } catch (e) {
              console.warn("Could not load fallback subjects:", e);
            }
          }
        }

        // Filter grades to only include those belonging to this school's subjects
        const subjectIds = new Set(allSubjects.map((s) => s.id));
        const schoolGrades = allGrades.filter((grade) => {
          const gradeSubjectId = grade.subjectId || grade.subject_id;
          return gradeSubjectId && subjectIds.has(gradeSubjectId);
        });

        return {
          ...school,
          classes: classesWithDetails,
          semesters: uniqueSemesters,
          selectedSemesters: selectedSemesters,
          grades: schoolGrades || [],
          subjects: allSubjects || [],
        };
      } catch (e) {
        console.error(`Error loading data for school ${school.schoolName}:`, e);
        return {
          ...school,
          semesters: [],
          selectedSemesters: [],
          grades: [],
          subjects: [],
        };
      }
    });

    try {
      const schoolsData = await Promise.all(schoolDataPromises);

      // --- SYNCHRONIZATION: Determine shared initial semester selection ---
      // We look for the "latest" semester number that is currently active across any school
      let maxCurrentSemesterNum = 0;

      schoolsData.forEach((school) => {
        school.selectedSemesters.forEach((semId) => {
          const sem = school.semesters.find((s) => s.id === semId);
          if (sem) {
            const num = getSemesterNumber(sem.name);
            if (num > maxCurrentSemesterNum) maxCurrentSemesterNum = num;
          }
        });
      });

      // If we found a current semester, synchronize all schools to show that semester number
      if (maxCurrentSemesterNum > 0) {
        schoolsData.forEach((school) => {
          // Find semester(s) with matching number
          const newSelected = school.semesters
            .filter((s) => getSemesterNumber(s.name) === maxCurrentSemesterNum)
            .map((s) => s.id);

          // Only override if the school actually HAS that semester.
          // Otherwise, we keep its default selection (or empty if it had none)
          if (newSelected.length > 0) {
            school.selectedSemesters = newSelected;
          }
        });
      }
      // -------------------------------------------------------------------

      setSchoolData(schoolsData);

      // Initialize overall selected semesters (ALL semesters for overall chart)
      const allSemesterIds = schoolsData.flatMap((school) =>
        school.semesters.map((s) => s.id),
      );
      const uniqueSemesterIds = [...new Set(allSemesterIds)];
      setOverallSelectedSemesters(uniqueSemesterIds);
    } catch (e) {
      console.error("Error loading school data:", e);
      setError("Failed to load school data.");
    } finally {
      // Clear all loading states
      setLoadingStates({});
    }
  };

  // Helper functions for overall chart
  const getOverallFilteredGrades = () => {
    if (overallSelectedSemesters.length === 0) {
      return [];
    }

    const allGrades = schoolData.flatMap((school) => school.grades);
    const allSemesters = schoolData.flatMap((school) => school.semesters);

    return allGrades.filter((grade) => {
      // Try to match by semesterId first
      if (
        grade.semesterId &&
        overallSelectedSemesters.includes(grade.semesterId)
      ) {
        return true;
      }

      // Fallback: match by semester name/code
      return allSemesters.some((semester) => {
        if (!overallSelectedSemesters.includes(semester.id)) return false;

        const possibleSemesterValues = [
          semester.name,
          semester.semesterCode,
          semester.shortName,
          `S${semester.id}`,
        ].filter((val) => val !== undefined && val !== null);

        const possibleAcademicYears = [
          semester.schoolYear,
          "2024/25",
          "2025/26",
        ].filter((val) => val !== undefined && val !== null);

        const semesterMatch = possibleSemesterValues.includes(grade.semester);
        const yearMatch = possibleAcademicYears.includes(grade.academicYear);
        return semesterMatch && yearMatch;
      });
    });
  };

  // Helper functions for school-based data
  const getFilteredGradesForSchool = (schoolData) => {
    if (
      !schoolData.selectedSemesters ||
      schoolData.selectedSemesters.length === 0
    ) {
      return [];
    }

    return schoolData.grades.filter((grade) => {
      // Try to match by semesterId first
      if (
        grade.semesterId &&
        schoolData.selectedSemesters.includes(grade.semesterId)
      ) {
        return true;
      }

      // Fallback: match by semester name/code
      return schoolData.semesters.some((semester) => {
        if (!schoolData.selectedSemesters.includes(semester.id)) return false;

        const possibleSemesterValues = [
          semester.name,
          semester.semesterCode,
          semester.shortName,
          `S${semester.id}`,
        ].filter((val) => val !== undefined && val !== null);

        const possibleAcademicYears = [
          semester.schoolYear,
          "2024/25",
          "2025/26",
        ].filter((val) => val !== undefined && val !== null);

        const semesterMatch = possibleSemesterValues.includes(grade.semester);
        const yearMatch = possibleAcademicYears.includes(grade.academicYear);
        return semesterMatch && yearMatch;
      });
    });
  };

  /* const getSubjectAverageForSchool = (schoolData, subjectId) => {
    const subjectGrades = getGradesBySubjectForSchool(schoolData, subjectId);
    if (subjectGrades.length === 0) return null;

    const sum = subjectGrades.reduce(
      (acc, grade) => acc + (grade.gradeValue || 0),
      0
    );
    return (sum / subjectGrades.length).toFixed(2);
  }; */

  const getOverallAverageForSchool = (schoolData) => {
    const filteredGrades = getFilteredGradesForSchool(schoolData);
    if (filteredGrades.length === 0) return null;

    const sum = filteredGrades.reduce(
      (acc, grade) => acc + (grade.gradeValue || 0),
      0,
    );
    return (sum / filteredGrades.length).toFixed(2);
  };

  // New functions for semester-grouped subjects
  const getSubjectsBySemesterForSchool = (schoolData) => {
    const filteredGrades = getFilteredGradesForSchool(schoolData);
    const semesterGroups = new Map();

    // Get selected semesters in order
    const selectedSemesters = schoolData.semesters.filter((sem) =>
      schoolData.selectedSemesters.includes(sem.id),
    );

    selectedSemesters.forEach((semester) => {
      const semesterGrades = filteredGrades.filter((grade) => {
        // Try to match by semesterId first
        if (grade.semesterId === semester.id) return true;

        // Fallback: match by semester name/code
        const possibleSemesterValues = [
          semester.name,
          semester.semesterCode,
          semester.shortName,
          `S${semester.id}`,
        ].filter((val) => val !== undefined && val !== null);

        const possibleAcademicYears = [
          semester.schoolYear,
          "2024/25",
          "2025/26",
        ].filter((val) => val !== undefined && val !== null);

        const semesterMatch = possibleSemesterValues.includes(grade.semester);
        const yearMatch = possibleAcademicYears.includes(grade.academicYear);
        return semesterMatch && yearMatch;
      });

      if (semesterGrades.length > 0) {
        // Get unique subjects for this semester
        const subjectIds = [...new Set(semesterGrades.map((g) => g.subjectId))];
        const semesterSubjects = schoolData.subjects.filter((subject) =>
          subjectIds.includes(subject.id),
        );

        semesterGroups.set(semester.id, {
          semester,
          subjects: semesterSubjects,
          grades: semesterGrades,
        });
      }
    });

    return Array.from(semesterGroups.values());
  };

  const getGradesBySubjectAndSemester = (semesterGroup, subjectId) => {
    return semesterGroup.grades.filter(
      (grade) => grade.subjectId === subjectId,
    );
  };

  const getSubjectAverageForSemester = (semesterGroup, subjectId) => {
    const subjectGrades = getGradesBySubjectAndSemester(
      semesterGroup,
      subjectId,
    );
    if (subjectGrades.length === 0) return null;

    const sum = subjectGrades.reduce(
      (acc, grade) => acc + (grade.gradeValue || 0),
      0,
    );
    return (sum / subjectGrades.length).toFixed(2);
  };

  // Modal handlers
  const toggleSemesterModal = (identifier) => {
    setSemesterModalOpen(semesterModalOpen === identifier ? null : identifier);
  };

  const handleSemesterToggle = (identifier, semesterId) => {
    if (identifier === "overall") {
      // Toggle for overall chart
      setOverallSelectedSemesters((prev) =>
        prev.includes(semesterId)
          ? prev.filter((id) => id !== semesterId)
          : [...prev, semesterId],
      );
    } else {
      // Toggle for specific school and SYNCHRONIZE others by semester number
      setSchoolData((prev) => {
        // 1. Get details from the school that was clicked
        const triggeringSchool = prev.find((s) => s.schoolId === identifier);
        if (!triggeringSchool) return prev;

        const triggeringSemester = triggeringSchool.semesters.find(
          (s) => s.id === semesterId,
        );
        if (!triggeringSemester) return prev;

        const targetSemNumber = getSemesterNumber(triggeringSemester.name);

        // 2. Determine if we are selecting (true) or deselecting (false)
        const isCurrentlySelected =
          triggeringSchool.selectedSemesters.includes(semesterId);
        const shouldSelect = !isCurrentlySelected;

        // 3. Update ALL schools
        return prev.map((school) => {
          // Find matched semester ID(s) in this school for the target number
          const matchingIds = school.semesters
            .filter((s) => getSemesterNumber(s.name) === targetSemNumber)
            .map((s) => s.id);

          // If this school doesn't have the semester, keep it unchanged
          if (matchingIds.length === 0) return school;

          let newSelected = [...school.selectedSemesters];

          if (shouldSelect) {
            // Add
            matchingIds.forEach((id) => {
              if (!newSelected.includes(id)) newSelected.push(id);
            });
          } else {
            // Remove
            newSelected = newSelected.filter((id) => !matchingIds.includes(id));
          }

          return { ...school, selectedSemesters: newSelected };
        });
      });
    }
  };

  const selectAllSemesters = (identifier) => {
    if (identifier === "overall") {
      // Select all for overall chart
      const allSemesterIds = schoolData.flatMap((school) =>
        school.semesters.map((s) => s.id),
      );
      const uniqueSemesterIds = [...new Set(allSemesterIds)];
      setOverallSelectedSemesters(uniqueSemesterIds);
    } else {
      // Select all for ALL schools (Synchronized)
      setSchoolData((prev) =>
        prev.map((school) => ({
          ...school,
          selectedSemesters: school.semesters.map((s) => s.id),
        })),
      );
    }
  };

  const deselectAllSemesters = (identifier) => {
    if (identifier === "overall") {
      // Deselect all for overall chart
      setOverallSelectedSemesters([]);
    } else {
      // Deselect all for ALL schools (Synchronized)
      setSchoolData((prev) =>
        prev.map((school) => ({ ...school, selectedSemesters: [] })),
      );
    }
  };

  const formatGrade = (grade) => {
    return grade ? parseFloat(grade).toFixed(1) : "—";
  };

  const getProfessionIcon = (professionType) => {
    switch (professionType) {
      case "INFORMATIKER":
      case "INFORMATIK":
        return "fas fa-laptop-code";
      case "MEDIAMATIKER":
      case "MEDIAMATIK":
        return "fas fa-palette";
      case "KV":
        return "fas fa-file-invoice";
      case "KUNDENDIALOG":
        return "fas fa-headset";
      case "DETAILHANDEL":
        return "fas fa-store";
      default:
        return "fas fa-user-graduate";
    }
  };

  // Check if user is admin
  const isAdmin = () => user?.roles?.some((r) => r.name === "ADMIN");
  const isBerufsbildner = () =>
    user?.roles?.some((r) => r.name === "BERUFSBILDNER");

  const handleUnassign = async () => {
    // Confirmation is now handled by the modal
    try {
      await practiceInstructorService.unassignStudent(studentId);
      setSuccess("Student unassigned successfully.");
      setAssignMode(false);
      setCurrentInstructor(null);
      setStudent((prev) => ({ ...prev, activePracticeInstructor: null }));
    } catch (err) {
      setError("Failed to unassign student.");
      console.error(err);
    }
  };

  const handleAssignClick = async () => {
    if (assignMode) {
      setAssignMode(false);
      return;
    }
    setAssignMode(true);
    setAssignLoading(true);
    try {
      const [instructors, currentInstructorData] = await Promise.all([
        practiceInstructorService.getAvailableInstructors(studentId),
        practiceInstructorService.getCurrentInstructor(studentId).catch(() => null),
      ]);

      const mergedInstructors = [
        ...(instructors || []),
        ...(currentInstructorData ? [currentInstructorData] : []),
      ].filter(
        (instructor, index, array) =>
          instructor &&
          array.findIndex((candidate) => candidate?.id === instructor.id) === index,
      );

      setAvailableInstructors(mergedInstructors);
      setCurrentInstructor(currentInstructorData || null);
      setStudent((prev) => ({
        ...prev,
        activePracticeInstructor: currentInstructorData || null,
      }));
    } catch (e) {
      setError("Failed to load instructors");
      setAssignMode(false);
    } finally {
      setAssignLoading(false);
    }
  };

  const submitAssignment = async (instructorId) => {
    if (!instructorId) return;
    try {
      await practiceInstructorService.assignInstructor(studentId, instructorId);
      setSuccess("Instructor assigned successfully");
      setAssignMode(false);

      // Update local state
      const assigned = availableInstructors.find(
        (i) => i.id === parseInt(instructorId),
      );
      if (assigned) {
        setCurrentInstructor(assigned);
        setStudent((prev) => ({ ...prev, activePracticeInstructor: assigned }));
      } else {
        const instructor =
          await practiceInstructorService.getCurrentInstructor(studentId);
        setCurrentInstructor(instructor);
        setStudent((prev) => ({
          ...prev,
          activePracticeInstructor: instructor,
        }));
      }
    } catch (e) {
      setError(
        "Assignment failed: " + (e.response?.data?.message || e.message),
      );
    }
  };

  if (!hasAccess()) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-amber-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            Only administrators and instructors can view student details.
          </p>
        </div>
      </div>
    );
  }

  if (!student && loading) {
    return <SkeletonStudentDetailPage />;
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-user-times text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Student Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The requested student could not be found.
          </p>
          <button
            onClick={() =>
              navigate(
                isStrictlyPracticeInstructor
                  ? "/practice-instructor/students"
                  : "/students",
              )
            }
            className="btn-brand"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            {isStrictlyPracticeInstructor
              ? "Back to My Students"
              : "Back to Apprentice Directory"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={() =>
              navigate(
                isStrictlyPracticeInstructor
                  ? "/practice-instructor/students"
                  : "/students",
              )
            }
            className="mb-4 flex items-center text-sunrise-red hover:text-sunrise-600 transition-colors"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            {isStrictlyPracticeInstructor
              ? "Back to My Students"
              : "Back to Apprentice Directory"}
          </button>

          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-gradient-to-br from-sunrise-red to-sunrise-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-6">
                  {getInitials(student.firstName, student.lastName, student.username)}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-grey-dusk mb-2">
                    {student.firstName} {student.lastName}
                  </h1>
                  <p className="text-gray-600 text-lg">@{student.username}</p>
                  <p className="text-gray-500 text-sm">{student.email}</p>
                </div>
              </div>
              <div className="mt-4 lg:mt-0 flex flex-col items-start lg:items-end space-y-2">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getProfessionPillClasses(
                    student.professionType,
                  )}`}
                >
                  <i
                    className={`${getProfessionIcon(
                      student.professionType,
                    )} mr-2`}
                  ></i>
                  {student.professionType || "Unknown"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Quick Actions */}
        {(isAdmin() || isBerufsbildner()) && student && (
          <div className="mb-6">
            <div className="space-y-6">
              {/* Action Cards */}
 {/* Top action cards: 4 clean equal cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {isAdmin() && (
                  <button
                    onClick={() =>
                      navigate(`/admin/learning-documentation/${studentId}`)
                    }
                    className="card-brand p-5 hover:shadow-md transition-all duration-200 group text-left min-h-[104px]"
                  >
                    <div className="flex items-center justify-between h-full gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 bg-sunrise-red/10 rounded-2xl flex items-center justify-center text-sunrise-red group-hover:bg-sunrise-red group-hover:text-white transition-colors duration-200 flex-shrink-0">
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="text-lg font-semibold text-grey-dusk truncate">
                            Documentation
                          </div>
                          <div className="text-sm text-gray-500">
                            View entries
                          </div>
                        </div>
                      </div>

                      <i className="fas fa-chevron-right text-gray-400 group-hover:text-sunrise-red group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0"></i>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => {
                    sessionStorage.setItem(
                      `student-${student.id}`,
                      JSON.stringify(student),
                    );
                    navigate(`/trainer/semester-reviews?student=${studentId}`);
                  }}
                  className="card-brand p-5 hover:shadow-md transition-all duration-200 group text-left min-h-[104px]"
                >
                  <div className="flex items-center justify-between h-full gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 bg-sunrise-red/10 rounded-2xl flex items-center justify-center text-sunrise-red group-hover:bg-sunrise-red group-hover:text-white transition-colors duration-200 flex-shrink-0">
                        <i className="fas fa-clipboard-list"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg font-semibold text-grey-dusk truncate">
                          Reviews
                        </div>
                        <div className="text-sm text-gray-500">
                          Semester evaluations
                        </div>
                      </div>
                    </div>

                    <i className="fas fa-chevron-right text-gray-400 group-hover:text-sunrise-red group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0"></i>
                  </div>
                </button>

                <button
                  onClick={handleAssignClick}
                  className={`card-brand p-5 hover:shadow-md transition-all duration-200 group text-left min-h-[104px] ${
                    assignMode ? "ring-2 ring-sunrise-red/30" : ""
                  }`}
                >
                  <div className="flex items-center justify-between h-full gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 bg-sunrise-red/10 rounded-2xl flex items-center justify-center text-sunrise-red group-hover:bg-sunrise-red group-hover:text-white transition-colors duration-200 flex-shrink-0">
                        <i className="fas fa-user-tag"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg font-semibold text-grey-dusk truncate">
                          Practice Instr.
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {currentInstructor
                            ? `${currentInstructor.firstName} ${currentInstructor.lastName}`
                            : "Assign instructor"}
                        </div>
                      </div>
                    </div>

                    <i className="fas fa-chevron-right text-gray-400 group-hover:text-sunrise-red group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0"></i>
                  </div>
                </button>

                <button
                  onClick={() => setGradeReportModalOpen(true)}
                  className="card-brand p-5 hover:shadow-md transition-all duration-200 group text-left min-h-[104px]"
                >
                  <div className="flex items-center justify-between h-full gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 bg-sunrise-red/10 rounded-2xl flex items-center justify-center text-sunrise-red group-hover:bg-sunrise-red group-hover:text-white transition-colors duration-200 flex-shrink-0">
                        <i className="fas fa-file-invoice"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg font-semibold text-grey-dusk truncate">
                          Grade Report
                        </div>
                        <div className="text-sm text-gray-500">
                          Official sheet
                        </div>
                      </div>
                    </div>

                    <i className="fas fa-chevron-right text-gray-400 group-hover:text-sunrise-red group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0"></i>
                  </div>
                </button>
              </div>

              {/* Reports Section */}
              {(!isStrictlyPracticeInstructor || canViewGrades) && student && (
                <div className="card-brand overflow-hidden">
                    <ReportsList learnerId={student.id} isOwnProfile={false} />
                </div>
              )}
            </div>

            <PracticeInstructorSelectModal
              isOpen={assignMode}
              onClose={() => setAssignMode(false)}
              onAssign={submitAssignment}
              onUnassign={handleUnassign}
              student={student}
              availableInstructors={availableInstructors}
              isLoading={assignLoading}
            />

            <StudentGradeReportModal
              isOpen={gradeReportModalOpen}
              onClose={() => setGradeReportModalOpen(false)}
              student={student}
              schoolData={schoolData}
            />
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-check-circle text-green-500 mr-3"></i>
                {success}
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="text-green-500 hover:text-green-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                {error}
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Grade Access Warning Area */}
        {isStrictlyPracticeInstructor && !canViewGrades && !loading && (
          <div className="mb-8">
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className="fas fa-history text-orange-500 text-xl"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-bold text-orange-800 uppercase tracking-wider mb-1">
                    Past Assignment
                  </p>
                  <h3 className="text-lg font-bold text-gray-900">
                    Grade Visibility Restricted
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Because you are no longer the active practice instructor for{" "}
                    {student?.firstName}, detailed grade information and module
                    progress are hidden. You can still view the basic profile
                    and your authored reviews.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overall Grades Chart - All Schools Combined */}
        {(!isStrictlyPracticeInstructor || canViewGrades) &&
          schoolData.length > 0 &&
          !Object.values(loadingStates).some((state) => state) && (
            <div className="mb-8 relative">
              {/* Chart Header with Filter */}
              <div className="card-brand-elevated mb-2 bg-white">
                <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud text-white p-4 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold flex items-center">
                        <i className="fas fa-chart-line mr-3"></i>
                        Overall Grade Progress - All Schools
                      </h2>
                      <p className="text-sm opacity-90 mt-1">
                        <i className="fas fa-chart-bar mr-1 text-blue-400"></i>
                        {overallSelectedSemesters.length} semester(s) selected -
                        Complete overview across all schools
                      </p>
                    </div>
                    <button
                      onClick={() => toggleSemesterModal("overall")}
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-3 transition-all duration-200 flex items-center justify-center"
                      title="Filter Semesters"
                    >
                      <i className="fas fa-filter text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <GradesChart
                grades={getOverallFilteredGrades()}
                subjects={schoolData
                  .flatMap((school) => school.subjects)
                  .filter(
                    (subject, index, self) =>
                      index === self.findIndex((s) => s.id === subject.id),
                  )}
                selectedSemester={null}
                hideChartOnMobile={true}
              />

              {/* Overall Semester Filter Modal */}
              {semesterModalOpen === "overall" && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={() => setSemesterModalOpen(null)}
                  />

                  {/* Modal */}
                  <div className="absolute top-0 right-0 bg-white rounded-lg shadow-2xl border border-gray-200 z-[10000] w-96 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-grey-dusk/10 to-slate-cloud/10">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-grey-dusk flex items-center">
                          <i className="fas fa-filter mr-2 text-grey-dusk"></i>
                          Filter Semesters - All Schools
                        </h3>
                        <button
                          onClick={() => setSemesterModalOpen(null)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <div className="p-4">
                      {/* Select All / Deselect All */}
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => selectAllSemesters("overall")}
                          className="flex-1 px-3 py-2 text-sm bg-grey-dusk/10 text-grey-dusk rounded-lg hover:bg-grey-dusk/20 transition-colors"
                        >
                          <i className="fas fa-check-double mr-1"></i>
                          Select All
                        </button>
                        <button
                          onClick={() => deselectAllSemesters("overall")}
                          className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <i className="fas fa-times-circle mr-1"></i>
                          Clear All
                        </button>
                      </div>

                      {/* Group by School */}
                      {schoolData.map((school) => (
                        <div key={school.schoolId} className="mb-4">
                          <div className="font-semibold text-grey-dusk mb-2 flex items-center">
                            <i className="fas fa-school mr-2 text-sunrise-red"></i>
                            {school.schoolName}
                          </div>
                          <div className="space-y-2 ml-6">
                            {school.semesters.length > 0 ? (
                              school.semesters.map((semester) => {
                                // Check if this semester is current based on class start date
                                const isCurrent =
                                  semester.classInfo?.startDate &&
                                  (() => {
                                    const currentSem = getCurrentSemester(
                                      semester.classInfo.startDate,
                                    );
                                    if (
                                      !currentSem ||
                                      !currentSem.semesterNumber
                                    )
                                      return false;
                                    const semesterNum = parseInt(
                                      semester.name?.match(/\d+/)?.[0] || "0",
                                    );
                                    return (
                                      semesterNum ===
                                        currentSem.semesterNumber &&
                                      currentSem.isActive
                                    );
                                  })();

                                return (
                                  <label
                                    key={`overall-${semester.id}-${semester.classId}`}
                                    className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={overallSelectedSemesters.includes(
                                        semester.id,
                                      )}
                                      onChange={() =>
                                        handleSemesterToggle(
                                          "overall",
                                          semester.id,
                                        )
                                      }
                                      className="w-4 h-4 text-grey-dusk bg-gray-100 border-gray-300 rounded focus:ring-grey-dusk focus:ring-2"
                                    />
                                    <div className="flex-1 flex items-center justify-between">
                                      <div className="font-medium text-grey-dusk text-sm">
                                        {isCurrent && (
                                          <i
                                            className="fas fa-circle text-green-500 mr-2 text-xs animate-pulse"
                                            title="Current Semester"
                                          ></i>
                                        )}
                                        {semester.name}
                                        {semester.className && (
                                          <span className="text-sunrise-red ml-2">
                                            - {semester.className}
                                          </span>
                                        )}
                                      </div>
                                      {isCurrent && (
                                        <span className="text-green-600 text-xs font-semibold bg-green-100 px-2 py-0.5 rounded-full">
                                          Current
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                );
                              })
                            ) : (
                              <div className="text-gray-500 text-sm py-2">
                                No semesters available
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

        {/* School Cards */}
        {(!isStrictlyPracticeInstructor || canViewGrades) &&
          (Object.values(loadingStates).some((state) => state) ? (
            // Show skeleton while loading school data
            <div className="space-y-8">
              {[1, 2].map((i) => (
                <div key={i} className="card-brand animate-pulse">
                  <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 p-6 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="h-6 bg-white/20 rounded w-48 mb-2"></div>
                        <div className="h-4 bg-white/10 rounded w-64"></div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div>
                          <div className="h-4 bg-white/10 rounded w-24 mb-1"></div>
                          <div className="h-8 bg-white/20 rounded w-16"></div>
                        </div>
                        <div className="w-12 h-12 bg-white/20 rounded-lg"></div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="space-y-6">
                      <div className="h-64 bg-slate-cloud/10 rounded"></div>
                      <div className="space-y-3">
                        <div className="h-12 bg-slate-cloud/10 rounded"></div>
                        <div className="h-10 bg-slate-cloud/5 rounded"></div>
                        <div className="h-10 bg-slate-cloud/5 rounded"></div>
                        <div className="h-10 bg-slate-cloud/5 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : schoolData.length === 0 ? (
            // Show "No Schools" only after loading is complete
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-slate-cloud/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-school text-slate-cloud text-2xl"></i>
              </div>
              <h3 className="text-lg font-medium text-grey-dusk mb-2">
                No Schools Found
              </h3>
              <p className="text-slate-cloud">
                This student is not enrolled in any schools.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {schoolData.map((school) => (
                <div key={school.schoolId} className="card-brand relative">
                  {/* School Header */}
                  <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white p-6 rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h2 className="text-xl font-bold flex items-center mb-2">
                          <i className="fas fa-school mr-3"></i>
                          {school.schoolName}
                          {school.selectedSemesters.length > 0 && (
                            <span className="text-base font-normal ml-3 opacity-90">
                              -{" "}
                              {school.semesters
                                .filter((s) =>
                                  school.selectedSemesters.includes(s.id),
                                )
                                .map((s) => s.name)
                                .join(", ")}
                            </span>
                          )}
                        </h2>
                        <div className="text-sm opacity-90">
                          <span className="mr-4">
                            <i className="fas fa-chalkboard-teacher mr-1"></i>
                            {school.classes.length}{" "}
                            {school.classes.length === 1 ? "Class" : "Classes"}
                          </span>
                          <span className="mr-4">
                            <i className="fas fa-calendar-alt mr-1"></i>
                            {school.semesters.length}{" "}
                            {school.semesters.length === 1
                              ? "Semester"
                              : "Semesters"}
                          </span>
                          <span>
                            <i className="fas fa-calendar-check mr-1"></i>
                            {school.selectedSemesters.length} selected
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-sm opacity-90">
                            Overall Average
                          </div>
                          <div className="text-2xl font-bold">
                            {formatGrade(getOverallAverageForSchool(school)) ||
                              "—"}
                          </div>
                        </div>
                        {/* Semester Filter Button */}
                        <button
                          onClick={() => toggleSemesterModal(school.schoolId)}
                          className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-3 transition-all duration-200 flex items-center justify-center"
                          title="Filter Semesters"
                        >
                          <i className="fas fa-filter text-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Loading State with Skeleton */}
                  {loadingStates[school.schoolId] && (
                    <div className="p-6 space-y-4 animate-pulse">
                      <div className="h-64 bg-gray-100 rounded"></div>
                      <div className="h-48 bg-gray-50 rounded"></div>
                    </div>
                  )}

                  {/* Grades Content */}
                  {!loadingStates[school.schoolId] && (
                    <div className="p-6">
                      {getFilteredGradesForSchool(school).length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-chart-line text-gray-400 text-2xl"></i>
                          </div>
                          <h3 className="text-lg font-medium text-gray-600 mb-2">
                            No grades found
                          </h3>
                          <p className="text-gray-500">
                            {school.selectedSemesters.length === 0
                              ? "No semesters are selected. Use the filter button to select semesters."
                              : "No grades are available for the selected semesters."}
                          </p>
                        </div>
                      ) : (
                        <div>
                          {/* Grades Chart */}
                          <GradesChart
                            grades={getFilteredGradesForSchool(school)}
                            subjects={school.subjects}
                            selectedSemester={null}
                          />

                          {/* Grades Table */}
                          <div className="overflow-x-auto mt-8">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b-2 border-gray-200">
                                  <th className="text-left py-3 px-4 font-semibold text-grey-dusk">
                                    Subject
                                  </th>
                                  <th className="text-center py-3 px-4 font-semibold text-grey-dusk">
                                    Grades
                                  </th>
                                  <th className="text-center py-3 px-4 font-semibold text-grey-dusk">
                                    Average
                                  </th>
                                  <th className="text-center py-3 px-4 font-semibold text-grey-dusk">
                                    Count
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getSubjectsBySemesterForSchool(school).map(
                                  (semesterGroup, semesterIndex) => (
                                    <React.Fragment
                                      key={semesterGroup.semester.id}
                                    >
                                      {/* Semester Divider */}
                                      <tr className="bg-gradient-to-r from-sunrise-red/10 to-sunrise-600/10">
                                        <td colSpan="4" className="py-3 px-4">
                                          <div className="flex items-center">
                                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-sunrise-red/30 to-transparent"></div>
                                            <div className="px-4 flex items-center">
                                              <i className="fas fa-calendar-alt text-sunrise-red mr-2"></i>
                                              <span className="font-semibold text-grey-dusk text-sm">
                                                {semesterGroup.semester.name}
                                                {semesterGroup.semester
                                                  .className && (
                                                  <span className="text-sunrise-red ml-2">
                                                    -{" "}
                                                    {
                                                      semesterGroup.semester
                                                        .className
                                                    }
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                            <div className="flex-1 h-px bg-gradient-to-r from-sunrise-red/30 via-sunrise-red/30 to-transparent"></div>
                                          </div>
                                        </td>
                                      </tr>

                                      {/* Subjects for this semester */}
                                      {semesterGroup.subjects.map((subject) => {
                                        const subjectGrades =
                                          getGradesBySubjectAndSemester(
                                            semesterGroup,
                                            subject.id,
                                          );
                                        const average =
                                          getSubjectAverageForSemester(
                                            semesterGroup,
                                            subject.id,
                                          );

                                        return (
                                          <tr
                                            key={`${semesterGroup.semester.id}-${subject.id}`}
                                            className="border-b border-gray-100 hover:bg-gray-50"
                                          >
                                            <td className="py-4 px-4 pl-8">
                                              <div>
                                                <h3 className="font-semibold text-grey-dusk">
                                                  {subject.subjectCode
                                                    ? `${subject.subjectCode} - ${subject.name}`
                                                    : subject.name}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                  {subject.description}
                                                </p>
                                              </div>
                                            </td>
                                            <td className="py-4 px-4">
                                              <div className="flex flex-wrap justify-center gap-2">
                                                {subjectGrades.map(
                                                  (grade, index) => (
                                                    <span
                                                      key={index}
                                                      className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-gray-100"
                                                      style={{
                                                        color: getGradeColor(
                                                          grade.gradeValue,
                                                        ),
                                                      }}
                                                      title={`Exam: ${
                                                        grade.examType || "N/A"
                                                      } | Date: ${formatDate(
                                                        grade.examDate,
                                                      )} | Semester: ${
                                                        semesterGroup.semester
                                                          .name
                                                      }`}
                                                    >
                                                      {formatGrade(
                                                        grade.gradeValue,
                                                      )}
                                                    </span>
                                                  ),
                                                )}
                                              </div>
                                            </td>
                                            <td
                                              className="py-4 px-4 text-center text-lg font-bold"
                                              style={{
                                                color: getGradeColor(average),
                                              }}
                                            >
                                              {formatGrade(average)}
                                            </td>
                                            <td className="py-4 px-4 text-center text-gray-600">
                                              {subjectGrades.length}
                                            </td>
                                          </tr>
                                        );
                                      })}

                                      {/* Add some spacing between semester groups (except for the last one) */}
                                      {semesterIndex <
                                        getSubjectsBySemesterForSchool(school)
                                          .length -
                                          1 && (
                                        <tr>
                                          <td colSpan="4" className="py-2"></td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Semester Filter Modal */}
                  {semesterModalOpen === school.schoolId && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setSemesterModalOpen(null)}
                      />

                      {/* Modal */}
                      <div className="absolute top-0 right-0 bg-white rounded-l-lg shadow-2xl border border-gray-200 z-[10000] w-80 max-h-96 overflow-y-auto">
                        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-sunrise-red/10 to-sunrise-600/10">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-grey-dusk flex items-center">
                              <i className="fas fa-filter mr-2 text-sunrise-red"></i>
                              Filter Semesters
                            </h3>
                            <button
                              onClick={() => setSemesterModalOpen(null)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>

                        <div className="p-4">
                          {/* Select All / Deselect All */}
                          <div className="flex gap-2 mb-4">
                            <button
                              onClick={() =>
                                selectAllSemesters(school.schoolId)
                              }
                              className="flex-1 px-3 py-2 text-sm bg-sunrise-red/10 text-sunrise-red rounded-lg hover:bg-sunrise-red/20 transition-colors"
                            >
                              <i className="fas fa-check-double mr-1"></i>
                              Select All
                            </button>
                            <button
                              onClick={() =>
                                deselectAllSemesters(school.schoolId)
                              }
                              className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <i className="fas fa-times-circle mr-1"></i>
                              Clear All
                            </button>
                          </div>

                          {/* Semester Checkboxes */}
                          <div className="space-y-2">
                            {school.semesters.length > 0 ? (
                              school.semesters.map((semester) => {
                                // Check if this semester is current based on class start date
                                const isCurrent =
                                  semester.classInfo?.startDate &&
                                  (() => {
                                    const currentSem = getCurrentSemester(
                                      semester.classInfo.startDate,
                                    );
                                    if (
                                      !currentSem ||
                                      !currentSem.semesterNumber
                                    )
                                      return false;
                                    const semesterNum = parseInt(
                                      semester.name?.match(/\d+/)?.[0] || "0",
                                    );
                                    return (
                                      semesterNum ===
                                        currentSem.semesterNumber &&
                                      currentSem.isActive
                                    );
                                  })();

                                return (
                                  <label
                                    key={`${semester.id}-${semester.classId}`}
                                    className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={school.selectedSemesters.includes(
                                        semester.id,
                                      )}
                                      onChange={() =>
                                        handleSemesterToggle(
                                          school.schoolId,
                                          semester.id,
                                        )
                                      }
                                      className="w-4 h-4 text-sunrise-red bg-gray-100 border-gray-300 rounded focus:ring-sunrise-red focus:ring-2"
                                    />
                                    <div className="flex-1 flex items-center justify-between">
                                      <div className="font-medium text-grey-dusk">
                                        {isCurrent && (
                                          <i
                                            className="fas fa-circle text-green-500 mr-2 text-xs animate-pulse"
                                            title="Current Semester"
                                          ></i>
                                        )}
                                        {semester.name}
                                        {semester.className && (
                                          <span className="text-sunrise-red ml-2">
                                            - {semester.className}
                                          </span>
                                        )}
                                      </div>
                                      {isCurrent && (
                                        <span className="text-green-600 text-xs font-semibold bg-green-100 px-2 py-0.5 rounded-full">
                                          Current
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                );
                              })
                            ) : (
                              <div className="text-gray-500 text-sm text-center py-4">
                                No semesters available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

export default StudentDetail;
